import React, { useState, useEffect, useCallback } from "react";
import { useDashboard } from "../../../../context/dashboardContext";

const IsoModal = ({ isOpen, onClose, imageFilename }) => {
  const { getLayoutImage } = useDashboard();
  const [isoImageUrl, setIsoImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);


  const loadIsoImage = useCallback(async () => {
  if (!imageFilename) return;

  setLoading(true);
  try {
    const baseName = imageFilename.replace(".jpg", "");

    // Check if this is an EX or SG style with FLIP
    const isEXStyle = baseName.includes("EX") && baseName.includes("FLIP");
    const isSGStyle = baseName.includes("SG") && baseName.includes("FLIP");

    let isoFilename;

    if (isEXStyle || isSGStyle) {
      // For EX and SG styles with FLIP, ISO comes before FLIP
      const baseWithoutFlip = baseName.replace("FLIP", "");
      isoFilename = `${baseWithoutFlip}ISOFLIP.jpg`;
    } else {
      // Standard pattern for other styles
      isoFilename = `${baseName}ISO.jpg`;

      // Handle regular FLIP cases (non-EX/SG)
      if (baseName.includes("FLIP")) {
        const baseWithoutFlip = baseName.replace("FLIP", "");
        isoFilename = `${baseWithoutFlip}ISO FLIP.jpg`;
      }
    }

    const imageResponse = await getLayoutImage(isoFilename);

    if (imageResponse?.url) {
      setIsoImageUrl(imageResponse.url);
    } else {
      console.error("ISO image not found:", isoFilename);

      let altIsoFilename;

      if (isEXStyle || isSGStyle) {
        // Try without the FLIP suffix first
        altIsoFilename = `${baseName.replace("FLIP", "")}ISO.jpg`;
      } else if (baseName.includes("FLIP")) {
        // Try with space in "ISO FLIP"
        altIsoFilename = `${baseName.replace("FLIP", "")}ISO FLIP.jpg`;
      } else {
        // Try standard pattern
        altIsoFilename = `${baseName}ISO.jpg`;
      }

      const altImageResponse = await getLayoutImage(altIsoFilename);
      if (altImageResponse?.url) {
        setIsoImageUrl(altImageResponse.url);
      }
    }
  } catch (error) {
    console.error("Error loading ISO image:", error);
  } finally {
    setLoading(false);
  }
}, [imageFilename, setIsoImageUrl, setLoading, getLayoutImage]);


  useEffect(() => {
    if (isOpen && imageFilename) {
      loadIsoImage();
    }
  }, [isOpen, imageFilename,loadIsoImage]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-[1000px] h-[570px] flex flex-col overflow-hidden">
        {/* header */}
        <div className="bg-gradient-to-r from-red-600 to-red-800 px-6 py-2 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white text-center flex-1">
            ISO View of ({imageFilename}):{" "}
            {imageFilename.replace(".jpg", "ISO.jpg")}
          </h3>
          <button onClick={onClose} className="text-white hover:text-gray-200 text-2xl ml-4" > &times; </button>
        </div>

        {/* body */}
        <div className="flex flex-1 gap-4 p-4">
          {/* left details panel */}
          <div className="w-1/3 bg-gray-100 p-4 rounded-lg border shadow-md">
            <h4 className="text-md font-semibold mb-4 text-center text-red-700"> Conveyor Module Details </h4>
            <div className="space-y-3">
              {[
                { label: "Sequence Number", value: "Example" },
                { label: "Module Type", value: "Example" },
                { label: "Powered Rollers", value: "Example" },
                { label: "Vertical Rollers", value: "Example" },
                { label: "Motor Assembly", value: "Example" },
              ].map((item, idx) => (
                <div key={idx}>
                  <label className="block text-xs font-medium text-gray-700">
                    {item.label}:
                  </label>
                  <div tabIndex="0" className="border px-2 py-1 rounded bg-white text-sm shadow-sm focus:outline-none focus:border-red-600" >
                    {item.value}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* right ISO image / loader */}
          <div className="flex-1 flex items-center justify-center border rounded-lg bg-gray-50 shadow-inner">
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-red-600"></div>
              </div>
            ) : isoImageUrl ? (
              <img
                src={isoImageUrl}
                alt={`No img - ${imageFilename.replace(/(\.[^/.]+)$/, 'ISO$1')}`}
                className="max-w-full max-h-full object-contain rounded-md shadow-md"
              />
            ) : (
              <div className="text-red-600 text-sm font-medium text-center">
                ISO image not available for this component
              </div>
            )}
          </div>
        </div>

        {/* footer */}
        <div className="bg-gray-100 p-3 flex justify-center">
          <button onClick={onClose} className="bg-gradient-to-r from-red-600 to-red-800 text-white px-5 py-1.5 rounded-md font-semibold shadow hover:opacity-90 transition text-sm" > Close </button>
        </div>
      </div>
    </div>
  );
};

export default IsoModal;
